import appConfig from '../../config/appConfig';
import * as storageKeys from '../../consts/app/storageKeys';
import {selectDiagnosticFlowDataResult, selectIsDiagnosticPhase2} from '../../state/selectors/advancedDiagnostic';
import {selectIotDeviceData} from '../../state/selectors/iotDevice';
import {getState} from '../../state/store';
import log from '../logger/log';
import localStorageClient from './localStorageClient';
import sessionStorageClient from './sessionStorageClient';

const getDeviceDataFromStorage = () => sessionStorageClient.getEncodedItem(storageKeys.DEVICE_DATA);
const removeDeviceDataFromStorage = () => sessionStorageClient.removeItem(storageKeys.DEVICE_DATA);
const setDeviceDataToStorage = (data) => sessionStorageClient.setDecodedItem(storageKeys.DEVICE_DATA, data);

const getAppInstanceIdFromStorage = () => localStorageClient.getItem(storageKeys.APP_INSTANCE_ID);
const setAppInstanceIdToStorage = (data) => localStorageClient.setItem(storageKeys.APP_INSTANCE_ID, data);

const setAccessTokenToStorage = (data) => localStorageClient.setDecodedItem(storageKeys.ACCESS_TOKEN, data);
const removeAccessTokenFromStorage = () => localStorageClient.removeItem(storageKeys.ACCESS_TOKEN);
const getAccessTokenFromStorage = () => localStorageClient.getEncodedItem(storageKeys.ACCESS_TOKEN);

const setAccessTokenExpirationToStorage = (data) =>
    localStorageClient.setDecodedItem(storageKeys.ACCESS_TOKEN_EXPIRATION, data);
const removeAccessTokenExpirationFromStorage = () => localStorageClient.removeItem(storageKeys.ACCESS_TOKEN_EXPIRATION);
const getAccessTokenExpirationFromStorage = () => {
    try {
        const date = localStorageClient.getEncodedItem(storageKeys.ACCESS_TOKEN_EXPIRATION);

        return new Date(date).getTime();
    } catch (e) {
        return null;
    }
};

const setRefreshTokenToStorage = (data) => localStorageClient.setDecodedItem(storageKeys.REFRESH_TOKEN, data);
const removeRefreshTokenFromStorage = () => localStorageClient.removeItem(storageKeys.REFRESH_TOKEN);
const getRefreshTokenFromStorage = () => localStorageClient.getEncodedItem(storageKeys.REFRESH_TOKEN);

const setSpiceTokenToStorage = (data) => localStorageClient.setDecodedItem(storageKeys.SPICE_TOKEN, data);
const removeSpiceTokenFromStorage = () => localStorageClient.removeItem(storageKeys.SPICE_TOKEN);
const getSpiceTokenFromStorage = () => localStorageClient.getEncodedItem(storageKeys.SPICE_TOKEN);

const setUsernameToStorage = (data) => localStorageClient.setDecodedItem(storageKeys.USER_NAME, data);
const removeUsernameFromStorage = () => localStorageClient.removeItem(storageKeys.USER_NAME);
const getUsernameFromStorage = () => localStorageClient.getEncodedItem(storageKeys.USER_NAME);

const setMarketCodeToLocalStorage = (data) => localStorageClient.setDecodedItem(storageKeys.MARKET_CODE, data);
const removeMarketCodeFromLocalStorage = () => localStorageClient.removeItem(storageKeys.MARKET_CODE);
const getMarketCodeFromLocalStorage = () => localStorageClient.getEncodedItem(storageKeys.MARKET_CODE);

const setLanguageCodeToLocalStorage = (data) => localStorageClient.setDecodedItem(storageKeys.LANGUAGE_CODE, data);
const removeLanguageCodeFromLocalStorage = () => localStorageClient.removeItem(storageKeys.LANGUAGE_CODE);
const getLanguageCodeFromLocalStorage = () => localStorageClient.getEncodedItem(storageKeys.LANGUAGE_CODE);

const getIsCompatiblePageOpenedFromStorage = () => sessionStorageClient.getEncodedItem(storageKeys.COMPATIBLE_PAGE);
const setIsCompatiblePageOpenedToStorage = (data) =>
    sessionStorageClient.setDecodedItem(storageKeys.COMPATIBLE_PAGE, data);
const removeIsCompatiblePageOpenedFromStorage = () => sessionStorageClient.removeItem(storageKeys.COMPATIBLE_PAGE);

const removeUserSessionDataFromStorage = () => {
    removeAccessTokenFromStorage();
    removeAccessTokenExpirationFromStorage();
    removeLanguageCodeFromLocalStorage();
    removeMarketCodeFromLocalStorage();
    removeRefreshTokenFromStorage();
    removeSpiceTokenFromStorage();
    removeUsernameFromStorage();
};

const getGamGigyaAccountDataFromStorage = () => localStorageClient.getItem(storageKeys.GAM_GIGYA_ACCOUNT_DATA);
const getGamGigyaJWTFromStorage = () => {
    const userData = getGamGigyaAccountDataFromStorage();
    const {gigyaJWT} = userData || {};

    return gigyaJWT;
};
const setGamGigyaAccountDataFromStorage = (data) =>
    localStorageClient.setItem(storageKeys.GAM_GIGYA_ACCOUNT_DATA, data);

const setFirstAppRunToLocalStorage = (data) => localStorageClient.setItem(storageKeys.FIRST_TIME_OPEN_APP, data);
const getFirstAppRunFromLocalStorage = () => localStorageClient.getItem(storageKeys.FIRST_TIME_OPEN_APP);

const getVapeSessionDisclaimerLocalStorage = () => localStorageClient.getItem(storageKeys.VAPE_SESSION_DISCLAIMER);
const setVapeSessionDisclaimerLocalStorage = (data) =>
    localStorageClient.setItem(storageKeys.VAPE_SESSION_DISCLAIMER, data);

const setUsageStatsDisclaimerToSessionStorage = (data) =>
    sessionStorageClient.setItem(storageKeys.USAGE_STATS_DISCLAIMER, data);
const getUsageStatsDisclaimerFromSessionStorage = () =>
    sessionStorageClient.getItem(storageKeys.USAGE_STATS_DISCLAIMER);

const setConnectionProgressToSessionStorage = (data) =>
    sessionStorageClient.setItem(storageKeys.DEVICE_CONNECTION_PROGRESS, data);
const getConnectionProgressFromSessionStorage = () =>
    sessionStorageClient.getItem(storageKeys.DEVICE_CONNECTION_PROGRESS);

const setFinishAdvancedDiagnosticsDataToLocalStorage = (data) => {
    const finishDeviceDiagnosticButtonDisplayTime = appConfig.getFinishDeviceDiagnosticButtonDisplayTime();

    // if the "finishDeviceDiagnosticButtonDisplayTime" param is set to 0 in a market config, then there is no finish device diagnostic flow
    if (finishDeviceDiagnosticButtonDisplayTime === 0) return;

    const {isHolder} = data || {};
    const state = getState();
    const iotProduct = selectIotDeviceData(state);
    const {isHolderConnected} = iotProduct?.device || {};
    const serialNumber = iotProduct[isHolder && isHolderConnected ? 'holder' : 'device']?.deviceSerialNumber;

    if (!serialNumber) return;

    const currentFinishAdvancedDiagnosticsData =
        localStorageClient.getEncodedItem(storageKeys.FINISH_ADVANCED_DIAGNOSTICS) || [];
    const itemToRemoveIndex = currentFinishAdvancedDiagnosticsData?.findIndex(
        (item) => item.serialNumber === serialNumber
    );

    const {holderCodentifyStorage} = selectDiagnosticFlowDataResult(state) || {};
    const isPhase2 = selectIsDiagnosticPhase2(state);
    const timestamp = finishDeviceDiagnosticButtonDisplayTime ? Date.now() : null;

    const updatedItem = {
        ...currentFinishAdvancedDiagnosticsData[itemToRemoveIndex],
        ...data,
        isPhase2,
        holderCodentify: holderCodentifyStorage,
        timestamp,
    };

    if (itemToRemoveIndex >= 0) {
        currentFinishAdvancedDiagnosticsData.splice(itemToRemoveIndex, 1);
    }

    const dataToStorage = {serialNumber, ...updatedItem};

    currentFinishAdvancedDiagnosticsData.push(dataToStorage);

    localStorageClient.setDecodedItem(storageKeys.FINISH_ADVANCED_DIAGNOSTICS, currentFinishAdvancedDiagnosticsData);
};

const getFinishAdvancedDiagnosticsDataFromLocalStorage = () => {
    const iotProduct = selectIotDeviceData(getState());
    const {device, holder} = iotProduct;
    const allFinishADData = localStorageClient.getEncodedItem(storageKeys.FINISH_ADVANCED_DIAGNOSTICS) || [];
    const finishADChargerData = allFinishADData.find((item) => item.serialNumber === device?.deviceSerialNumber);
    const finishADHolderData = allFinishADData.find((item) => item.serialNumber === holder?.deviceSerialNumber);

    return {finishADChargerData, finishADHolderData} || {};
};

const removeFinishAdvancedDiagnosticsDataFromLocalStorage = () => {
    const {finishADChargerData, finishADHolderData} = getFinishAdvancedDiagnosticsDataFromLocalStorage();
    const deviceSerialNumber = finishADChargerData?.serialNumber;
    const holderSerialNumber = finishADHolderData?.serialNumber;

    removeFinishAdvancedDiagnosticsDeviceData(deviceSerialNumber);
    removeFinishAdvancedDiagnosticsDeviceData(holderSerialNumber);
};

const removeFinishAdvancedDiagnosticsDeviceData = (serialNumber) => {
    const currentFinishAdvancedDiagnosticsData =
        localStorageClient.getEncodedItem(storageKeys.FINISH_ADVANCED_DIAGNOSTICS) || [];
    const itemToRemoveIndex = currentFinishAdvancedDiagnosticsData?.findIndex(
        (item) => item.serialNumber === serialNumber
    );

    if (itemToRemoveIndex >= 0) {
        currentFinishAdvancedDiagnosticsData.splice(itemToRemoveIndex, 1);
    }

    localStorageClient.setDecodedItem(storageKeys.FINISH_ADVANCED_DIAGNOSTICS, currentFinishAdvancedDiagnosticsData);
};

const getADAttemptsFromStorage = () =>
    localStorageClient.getEncodedItem(storageKeys.ADVANCED_DIAGNOSTICS_ATTEMPTS) || [];

const setADAttemptsToStorage = (data) => {
    localStorageClient.setDecodedItem(storageKeys.ADVANCED_DIAGNOSTICS_ATTEMPTS, data);
};

const getLeviaPopupsDataFromLocalStorage = () => {
    const result = localStorageClient.getEncodedItem(storageKeys.LEVIA_POPUPS);

    return result;
};

const setLeviaPopupsDataToLocalStorage = (data) => {
    localStorageClient.setDecodedItem(storageKeys.LEVIA_POPUPS, data);
};

const getLeviaPopupsDataFromLocalStorageBySerialNumber = (serialNumber) => {
    const data = getLeviaPopupsDataFromLocalStorage();

    return data?.[serialNumber] || {};
};

const setLeviaPopupsDataToLocalStorageBySerialNumber = ({serialNumber, ...rest}) => {
    const data = getLeviaPopupsDataFromLocalStorage();
    const dataToStore = {...data, [serialNumber]: {...data?.[serialNumber], ...rest}};
    const logValue = JSON.stringify(rest);

    log.info(
        `storageService: Set value to local storage. Key: ${storageKeys.LEVIA_POPUPS}, value: ${logValue}, deviceSerialNumber: ${serialNumber}`
    );

    setLeviaPopupsDataToLocalStorage(dataToStore);
};

const getFirmwareReleaseNotesDataFromLocalStorage = () => {
    const result = localStorageClient.getEncodedItem(storageKeys.FIRMWARE_RELATED_NOTES);

    return result;
};

const setFirmwareReleaseNotesDataToLocalStorage = (data) => {
    localStorageClient.setDecodedItem(storageKeys.FIRMWARE_RELATED_NOTES, data);
};

const getFirmwareReleaseNotesDataFromLocalStorageBySerialNumber = (serialNumber) => {
    const data = getFirmwareReleaseNotesDataFromLocalStorage();

    return data?.[serialNumber] || {};
};

const setFirmwareReleaseNotesDataToLocalStorageBySerialNumber = ({serialNumber, ...rest}) => {
    const data = getFirmwareReleaseNotesDataFromLocalStorage();
    const dataToStore = {...data, [serialNumber]: {...data?.[serialNumber], ...rest}};

    setFirmwareReleaseNotesDataToLocalStorage(dataToStore);
};

const removeFirmwareReleaseNotesData = (serialNumber, properties) => {
    const currentFirmwareReleaseNotesData = localStorageClient.getEncodedItem(storageKeys.FIRMWARE_RELATED_NOTES) || [];

    properties.forEach((property) => {
        delete currentFirmwareReleaseNotesData[serialNumber][property];
    });

    localStorageClient.setDecodedItem(storageKeys.FIRMWARE_RELATED_NOTES, currentFirmwareReleaseNotesData);
};

export default {
    getAccessTokenExpirationFromStorage,
    getAccessTokenFromStorage,
    getADAttemptsFromStorage,
    getAppInstanceIdFromStorage,
    getConnectionProgressFromSessionStorage,
    getDeviceDataFromStorage,
    getFinishAdvancedDiagnosticsDataFromLocalStorage,
    getFirstAppRunFromLocalStorage,
    getGamGigyaAccountDataFromStorage,
    getGamGigyaJWTFromStorage,
    getIsCompatiblePageOpenedFromStorage,
    getLanguageCodeFromLocalStorage,
    getMarketCodeFromLocalStorage,
    getRefreshTokenFromStorage,
    getSpiceTokenFromStorage,
    getUsageStatsDisclaimerFromSessionStorage,
    getUsernameFromStorage,
    getVapeSessionDisclaimerLocalStorage,
    getLeviaPopupsDataFromLocalStorageBySerialNumber,
    removeDeviceDataFromStorage,
    removeFinishAdvancedDiagnosticsDataFromLocalStorage,
    removeFinishAdvancedDiagnosticsDeviceData,
    removeIsCompatiblePageOpenedFromStorage,
    removeSpiceTokenFromStorage,
    removeUsernameFromStorage,
    removeUserSessionDataFromStorage,
    setAccessTokenExpirationToStorage,
    setAccessTokenToStorage,
    setADAttemptsToStorage,
    setAppInstanceIdToStorage,
    setConnectionProgressToSessionStorage,
    setDeviceDataToStorage,
    setFinishAdvancedDiagnosticsDataToLocalStorage,
    setFirstAppRunToLocalStorage,
    setGamGigyaAccountDataFromStorage,
    setIsCompatiblePageOpenedToStorage,
    setLanguageCodeToLocalStorage,
    setMarketCodeToLocalStorage,
    setRefreshTokenToStorage,
    setSpiceTokenToStorage,
    setUsageStatsDisclaimerToSessionStorage,
    setUsernameToStorage,
    setVapeSessionDisclaimerLocalStorage,
    setLeviaPopupsDataToLocalStorageBySerialNumber,
    getFirmwareReleaseNotesDataFromLocalStorageBySerialNumber,
    setFirmwareReleaseNotesDataToLocalStorageBySerialNumber,
    removeFirmwareReleaseNotesData,
};
